import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Users, Play, AlertTriangle, Loader2, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Button } from "@/components/ui/button";
import { useRole } from "@/contexts/RoleContext";
import { Navigate } from "react-router-dom";
import { CloserRequestModal } from "@/components/client/CloserRequestModal";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PageTour } from "@/components/tour/PageTour";
import { clientSalesSteps } from "@/lib/tour-steps";

interface CloserWithStats {
  id: string;
  name: string;
  email: string;
  status: string;
  weekly_target: number;
  performance_score: number | null;
  demosCompleted: number;
  conversionRate: number;
  lastDemoDate: string | null;
}

export default function ClientSales() {
  const { clientPackage, clientId } = useRole();
  const [closersData, setClosersData] = useState<CloserWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCloser, setSelectedCloser] = useState<string | null>(null);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [closerForRequest, setCloserForRequest] = useState<CloserWithStats | null>(null);

  useEffect(() => {
    if (!clientId || clientPackage !== 'elite') { setLoading(false); return; }

    const fetchClosers = async () => {
      setLoading(true);
      try {
        // Get closers assigned to this client
        const { data: assignments, error: assignError } = await supabase
          .from('closer_assignments')
          .select('closer_id')
          .eq('client_id', clientId)
          .eq('status', 'active');

        if (assignError) throw assignError;

        const closerIds = (assignments || []).map(a => a.closer_id);

        if (closerIds.length === 0) {
          setClosersData([]);
          setLoading(false);
          return;
        }

        // Get closer profiles
        const { data: closerProfiles, error: closerError } = await supabase
          .from('closers')
          .select('id, name, email, status, weekly_target, performance_score')
          .in('id', closerIds);

        if (closerError) throw closerError;

        // Get demo stats per closer for this client
        const { data: demos, error: demoError } = await supabase
          .from('demos')
          .select('closer_id, outcome, status, demo_date')
          .eq('client_id', clientId)
          .in('closer_id', closerIds);

        if (demoError) throw demoError;

        // Compute per-closer stats
        const closersWithStats: CloserWithStats[] = (closerProfiles || []).map(closer => {
          const closerDemos = (demos || []).filter(d => d.closer_id === closer.id);
          const completed = closerDemos.filter(d => d.status === 'completed');
          const closed = closerDemos.filter(d => d.outcome === 'closed');
          const sorted = closerDemos
            .filter(d => d.demo_date)
            .sort((a, b) => b.demo_date.localeCompare(a.demo_date));

          return {
            ...closer,
            demosCompleted: completed.length,
            conversionRate: completed.length > 0
              ? Math.round((closed.length / completed.length) * 100)
              : 0,
            lastDemoDate: sorted.length > 0 ? sorted[0].demo_date : null,
          };
        });

        setClosersData(closersWithStats);
      } catch (error) {
        console.error('Error fetching closers:', error);
        toast.error('Error loading sales team data');
      } finally {
        setLoading(false);
      }
    };

    fetchClosers();
  }, [clientId]);

  const handleRequestClick = (closer: CloserWithStats, e: React.MouseEvent) => {
    e.stopPropagation();
    setCloserForRequest(closer);
    setRequestModalOpen(true);
  };

  // Only Elite clients can access this page (guard placed after all hooks)
  if (clientPackage !== 'elite') {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (closersData.length === 0) {
    return (
      <motion.div className="space-y-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Sales Team</h1>
          <p className="text-muted-foreground">View your assigned closers and their performance</p>
        </div>
        <VentureCard>
          <VentureCardContent>
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No closers assigned to your account yet.</p>
              <p className="text-sm text-muted-foreground mt-2">Contact your account manager for assistance.</p>
            </div>
          </VentureCardContent>
        </VentureCard>
      </motion.div>
    );
  }

  return (
    <>
      <PageTour steps={clientSalesSteps} />
      <motion.div className="space-y-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Sales Team</h1>
          <p className="text-muted-foreground">View your assigned closers and their performance</p>
        </div>

        {/* Closer Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {closersData.map((closer) => (
            <VentureCard
              key={closer.id}
              className={`cursor-pointer transition-all ${selectedCloser === closer.id ? 'border-secondary' : ''}`}
              onClick={() => setSelectedCloser(closer.id)}
            >
              <VentureCardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{closer.name}</p>
                    <BadgeVenture variant={closer.status === 'active' ? 'success' : 'error'}>
                      {closer.status}
                    </BadgeVenture>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">Demos:</span>
                    <span>{closer.demosCompleted}</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Total demos this closer has conducted for your account</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">Conv:</span>
                    <span>{closer.conversionRate}%</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Percentage of demos that resulted in a closed deal</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="col-span-2"><span className="text-muted-foreground">Last:</span> {closer.lastDemoDate || 'No demos yet'}</div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={(e) => handleRequestClick(closer, e)}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Request Change
                </Button>
              </VentureCardContent>
            </VentureCard>
          ))}
        </div>

        {/* Demo Recordings — coming soon */}
        {selectedCloser && (
          <VentureCard>
            <VentureCardHeader>
              <VentureCardTitle>Demo Recordings - {closersData.find(c => c.id === selectedCloser)?.name}</VentureCardTitle>
            </VentureCardHeader>
            <VentureCardContent>
              <p className="text-sm text-muted-foreground text-center py-4">Demo recordings are not yet available.</p>
            </VentureCardContent>
          </VentureCard>
        )}
      </motion.div>

      {/* Request Modal */}
      <CloserRequestModal
        open={requestModalOpen}
        onOpenChange={setRequestModalOpen}
        closer={closerForRequest ? {
          id: closerForRequest.id,
          name: closerForRequest.name,
          demosCompleted: closerForRequest.demosCompleted,
          conversionRate: closerForRequest.conversionRate,
          lastDemoDate: closerForRequest.lastDemoDate || 'N/A',
          status: closerForRequest.status === 'active' ? 'active' : 'flagged',
        } : null}
        clientId={clientId || ''}
        clientName=""
      />
    </>
  );
}
